import { deepMerge } from 'js/utils/Utils'
import { lerp, clamp, roundEx } from 'js/utils/Maths'

import Common from 'js/_core/Common'
import EventHandler from 'js/modules/_base/events/helper/EventHandler'

const defaults = {
  lerpMultiplier: 0.15,
  deltalerpMultiplier: 0.1,
  deltaLerpMax: 60,
  deltaLerpMin: -60,
  handleName: 'MouseMove',
  digit: 100,
}

export default class MouseMove extends EventHandler {
  constructor(options = {}) {
    super()
    // this.$ctr = document.body
    this.$ctr = window
    this.mouseMoveHandler = []
    this.ticking = false
    this.canUpdate = false

    // 初期値
    const initPositionX = 0
    const initPositionY = 0

    this.position = {
      static: {
        x: initPositionX,
        y: initPositionY,
        normal: {
          x: 0.5,
          y: 0.5,
        },
        prev: {
          x: initPositionX,
          y: initPositionY,
        },
      },
      x: initPositionX,
      y: initPositionY,
      normal: {
        x: 0.5,
        y: 0.5,
      },
      delta: {
        x: 0,
        y: 0,
      },
    }
    this.size = {
      x: 0,
      y: 0,
      ratioX: 0,
      ratioY: 0,
    }
    deepMerge(this, defaults, options)
  }

  init() {
    this._setSize()
    this._addHandlers()
    this._addEventListeners()
  }

  destroy() {
    this._removeEventListeners()
    this._removeHandlers()
  }

  isTouchDevice() {
    const touchEvent = window.ontouchstart
    const touchPoints = window.navigator.maxTouchPoints

    const isToucheEvent = touchEvent !== undefined && 0 < touchPoints

    if (isToucheEvent && (Common.detection.isMobileDevice || Common.detection.isTabletDevice)) {
      return true
    } else {
      return false
    }
  }

  _addEventListeners() {
    if (this.isTouchDevice()) {
      this.$ctr.addEventListener('touchstart', this._onMousemove, { passive: true })
      this.$ctr.addEventListener('touchmove', this._onMousemove, { passive: true })
      this.$ctr.addEventListener('touchend', this._onMousemove, { passive: true })
    } else {
      this.$ctr.addEventListener('mousemove', this._onMousemove, { passive: true })
    }
  }

  _removeEventListeners() {
    if (this.isTouchDevice()) {
      this.$ctr.removeEventListener('touchstart', this._onMousemove)
      this.$ctr.removeEventListener('touchmove', this._onMousemove)
      this.$ctr.removeEventListener('touchend', this._onMousemove)
    } else {
      this.$ctr.removeEventListener('mousemove', this._onMousemove)
    }
  }

  _addHandlers() {
    Common.raf.addHandler(this._onUpdate.bind(this), this.handleName)
    Common.resize.addHandler(this._onResize.bind(this), this.handleName)
  }

  _removeHandlers() {
    Common.raf.removeHandler(this.handleName)
    Common.resize.removeHandler(this.handleName)
  }

  _setSize(bodyWidth, windowHeight) {
    if (this.$ctr instanceof HTMLElement) {
      this.size.x = this.size.x = this.$ctr.getBoundingClientRect().width
      this.size.y = this.size.y = this.$ctr.getBoundingClientRect().height
    } else {
      this.size.x = bodyWidth || document.body.clientWidth
      this.size.y = windowHeight || window.innerHeight
    }

    this.size.ratioX = 1 / this.size.x
    this.size.ratioY = 1 / this.size.y
  }

  _getPosition(event, pos) {
    if (this._useClientPositionIfNeeded()) {
      if (event.targetTouches && event.targetTouches[0]) {
        let touch
        if (event.touches) {
          touch = event.targetTouches[0]
          pos.x = touch.clientX
          pos.y = touch.clientY
        } else {
          pos.x = event.clientX
          pos.y = event.clientY
        }
      } else {
        pos.x = event.clientX || 0
        pos.y = event.clientY || 0
      }
    } else {
      pos.x = event.layerX
      pos.y = event.layerY
    }

    pos.normal.x = pos.x * this.size.ratioX || 0 // 0 ~ 1
    pos.normal.y = pos.y * this.size.ratioY || 0 // 0 ~ 1
    return pos
  }

  _useClientPositionIfNeeded() {
    return this.$ctr === window || this.$ctr === document.body
  }

  _onMousemove = (e) => {
    this._updateOnMousemove(e)
  }

  _updateOnMousemove(e) {
    this.updateMousemove(e)
  }

  updateMousemove(e) {
    if (!this.ticking) {
      requestAnimationFrame(() => {
        if (!this.canUpdate) {
          this.canUpdate = true
        }

        this.ticking = false
        this.position.static = this._getPosition(e, this.position.static)

        // this._mousemoveHandler()
      })

      this.ticking = true
    }
  }

  _mousemoveHandler() {
    this.mouseMoveHandler.forEach((item) => {
      item.task(this.position)
    })
  }

  checkMouseMove() {
    if (Math.abs(this.position.x - this.position.static.x) < 0.1 && Math.abs(this.position.y - this.position.static.y) < 0.1) {
      this.position.x = this.position.static.x
      this.position.y = this.position.static.y
      this.position.delta.x = 0
      this.position.delta.y = 0

      this.canUpdate = false

      this._updateHandler()
    } else {
      this.position.x = lerp(this.position.x, this.position.static.x, this.lerpMultiplier / Common.raf.fpsMultiplier)
      this.position.y = lerp(this.position.y, this.position.static.y, this.lerpMultiplier / Common.raf.fpsMultiplier)

      this.position.normal.x = lerp(this.position.normal.x, this.position.static.normal.x, this.lerpMultiplier / Common.raf.fpsMultiplier)
      this.position.normal.y = lerp(this.position.normal.y, this.position.static.normal.y, this.lerpMultiplier / Common.raf.fpsMultiplier)

      const deltaStatic = {
        x: this.position.static.x - this.position.x,
        y: this.position.static.y - this.position.y,
      }

      this.position.delta.x = lerp(this.position.delta.x, deltaStatic.x, this.deltalerpMultiplier / Common.raf.fpsMultiplier)
      this.position.delta.y = lerp(this.position.delta.y, deltaStatic.y, this.deltalerpMultiplier / Common.raf.fpsMultiplier)

      this.position.delta.x = clamp(this.position.delta.x, this.deltaLerpMin, this.deltaLerpMax)
      this.position.delta.y = clamp(this.position.delta.y, this.deltaLerpMin, this.deltaLerpMax)

      this.position.delta.x = roundEx(this.position.delta.x, this.digit)
      this.position.delta.y = roundEx(this.position.delta.y, this.digit)

      this._updateHandler()
    }
  }

  _updateHandler() {
    this.handler.forEach((item) => {
      item.task(this.position)
    })
  }

  _onUpdate() {
    if (!this.canUpdate) {
      return
    }
    this.checkMouseMove()
  }

  _onResize(width, height) {
    this._setSize()
  }
}